import axios from 'axios';

console.log('process.env.BACKEND_URL', process.env.REACT_APP_BACKEND_URL);
// Create axios instance with baseURL
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api',   // Development backend URL
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists in localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Use x-auth-token header which is what the backend likely expects
      config.headers['x-auth-token'] = token;
    }
    
    // Log all requests in development mode (debugging)
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request:`, { 
        method: config.method.toUpperCase(),
        url: config.url, 
        data: config.data,
        params: config.params
      });
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response:`, { 
        status: response.status, 
        url: response.config.url,
        data: response.data 
      });
    }
    
    return response;
  },
  (error) => {
    // Handle response errors
    if (error.response) {
      // Log errors in development mode
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ API Error:`, { 
          status: error.response.status, 
          url: error.config.url,
          data: error.response.data 
        });
      }
      
      // Handle 401 errors (Unauthorized)
      if (error.response.status === 401) {
        // Check if the error is not from the login endpoint
        if (!error.config.url.includes('/auth/login')) {
          console.log('Unauthorized access detected (not during login). Clearing token.');
          localStorage.removeItem('token');
          // Don't redirect during login attempts
          if (window.location.pathname !== '/login') {
            console.log('Redirecting to login page');
            window.location.href = '/login';
          }
        } else {
          console.log('Login attempt failed with 401');
        }
      }
    } else if (error.request) {
      // Network error or no response
      console.error('Network Error - No response received:', error.request);
    } else {
      // Error in setting up the request
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API service functions
const apiService = {
  // Auth endpoints
  auth: {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    getProfile: () => api.get('/auth/me'),
    resetPassword: (email) => api.post('/auth/reset-password', { email }),
  },
  
  // Users endpoints
  users: {
    getAll: () => api.get('/users'),
    getById: (id) => api.get(`/users/${id}`),
    create: (userData) => api.post('/users', userData),
    update: (id, userData) => api.put(`/users/${id}`, userData),
    delete: (id) => api.delete(`/users/${id}`),
  },
  
  // Drivers endpoints
  drivers: {
    getAll: () => api.get('/drivers'),
    getById: (id) => api.get(`/drivers/${id}`),
    create: (driverData) => api.post('/drivers', driverData),
    update: (id, driverData) => api.put(`/drivers/${id}`, driverData),
    delete: (id) => api.delete(`/drivers/${id}`),
    updateStatus: (id, statusData) => api.patch(`/drivers/${id}/status`, statusData),
  },
  
  // Vans endpoints
  vans: {
    getAll: () => api.get('/vans'),
    getById: (id) => api.get(`/vans/${id}`),
    create: (vanData) => api.post('/vans', vanData),
    update: (id, vanData) => api.put(`/vans/${id}`, vanData),
    delete: (id) => api.delete(`/vans/${id}`),
    updateStatus: (id, status) => api.patch(`/vans/${id}/status`, { status }),
    addMaintenance: (id, maintenanceData) => api.post(`/vans/${id}/maintenance`, maintenanceData),
    getStats: (params) => api.get('/van-reports/stats', { params }),
    getDetails: (params) => api.get('/van-reports/details', { params }),
  },
  
  // Jobs endpoints
  jobs: {
    getAll: (params) => api.get('/jobs', { params }),
    getAllForDropdown: (params) => api.get('/jobs/dropdown', { params }),
    getReconciliationJobs: (params) => api.get('/jobs/reconciliation', { params }),
    getById: (id) => api.get(`/jobs/${id}`),
    create: (jobData) => api.post('/jobs', jobData),
    update: (id, jobData) => api.put(`/jobs/${id}`, jobData),
    delete: (id) => api.delete(`/jobs/${id}`),
    updateStatus: (id, status) => api.patch(`/jobs/${id}/status`, { jobStatus: status }),
    updatePayment: (id, paymentData) => api.patch(`/jobs/${id}/payment`, paymentData),
    batchUpdatePayment: (jobIds, paymentData) => api.patch('/jobs/batch/payment', { jobIds, ...paymentData }),
    assignDriver: (id, driverId) => api.patch(`/jobs/${id}/assign-driver`, { driver: driverId }),
    assignVan: (id, vanId) => api.patch(`/jobs/${id}/assign-van`, { van: vanId }),
    generateInvoiceNumber: () => api.get('/payments/generate-invoice-number'),
    getReport: (params) => api.get('/jobs/report', { params, responseType: 'blob' }),
    uploadReconciliationFile: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/jobs/reconciliation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
  },
  
  // Payments endpoints
  payments: {
    getAll: (params = {}) => api.get('/payments', { params }),
    getById: (id) => api.get(`/payments/${id}`),
    create: (data) => api.post('/payments', data),
    update: (id, data) => api.put(`/payments/${id}`, data),
    updateStatus: (id, data) => api.put(`/payments/${id}/status`, data),
    delete: (id) => api.delete(`/payments/${id}`),
    generateInvoiceNumber: () => api.get('/payments/generate-invoice-number'),
  },
  
  // Server status check - useful for diagnostics
  server: {
    checkStatus: () => api.get('/health'),
  },

  channels: {
    getAll: () => api.get('/channels'),
    getActive: () => api.get('/channels/active'),
    getById: (id) => api.get(`/channels/${id}`),
    create: (data) => api.post('/channels', data),
    update: (id, data) => api.put(`/channels/${id}`, data),
    delete: (id) => api.delete(`/channels/${id}`)
  },

  // Reports endpoints
  reports: {
    getFinancialReport: (params) => {
      // If format is json, return JSON response, otherwise return blob for CSV/Excel
      const responseType = params.format === 'json' ? 'json' : 'blob';
      return api.get('/reports/financial', { params, responseType });
    },
    getDashboardSummary: () => api.get('/reports/dashboard-summary'),
    getVanReport: (params) => api.get('/reports/van', { params }),
    downloadVanProfitabilityReport: (params) => api.get('/reports/van/profitability-report', { params, responseType: 'blob' }),
  },

  invoices: {
    getAll: (params) => api.get('/invoices', { params }),
    getById: (id) => api.get(`/invoices/${id}`),
    create: (data) => api.post('/invoices', data),
    update: (id, data) => api.put(`/invoices/${id}`, data),
    delete: (id) => api.delete(`/invoices/${id}`),
    downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob', headers: { Accept: 'application/pdf' } }),
    markPaid: (id) => api.put(`/invoices/${id}/mark-paid`),
  },

  customers: {
    getUnique: () => api.get('/jobs/customers/unique'),
  },

  // Insights endpoints
  insights: {
    getDashboard: () => api.get('/insights/dashboard'),
    getRevenue: (params) => api.get('/insights/revenue', { params }),
    getJobs: (params) => api.get('/insights/jobs', { params }),
    getFinancial: (params) => api.get('/insights/financial', { params }),
    getResources: (params) => api.get('/insights/resources', { params }),
    getPredictions: () => api.get('/insights/predictions'),
  },
};

export const downloadInvoicePDF = async (id) => {
  try {
    const response = await apiService.invoices.downloadPDF(id); // axios with responseType: 'blob'
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice-${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw new Error('Failed to download PDF. Please try again.');
  }
};

export default apiService; 
